/** Sign-in asked only when it is needed (render, ads, history). Google in production; a name field on a dev server. */
import { useCallback, useId, useRef, useState } from "react";
import { adsEnabled } from "../ads/enabled";
import { ApiError, type Api, type Me } from "../api";
import { config } from "../config";
import { FREE_DAILY, LEGAL, errorMessage } from "../messages";
import { profileFromIdToken, type Profile } from "../profile";
import { clearRef, pendingRef } from "../referral";
import { writeLocal } from "../storage";
import { GoogleSignIn } from "./GoogleSignIn";
import { Modal } from "./Modal";

interface Props {
  api: Api;
  cost: number;
  /** A render is waiting for the sign-in: it is sent as soon as the dialog closes signed in. */
  forRender: boolean;
  devUser?: string;
  /** `profile`: name / email / picture for the top-right corner (from the Google token, or the dev name). */
  onSignedIn: (me: Me, profile: Profile | null, devUser?: string) => void;
  onClose: () => void;
}

/** A friend's referral code kept from a `/?ref=` link, as the login calls' last argument (none: no argument). */
const withRef = (): [] | [string] => {
  const ref = pendingRef();
  return ref ? [ref] : [];
};

export function LoginDialog({ api, cost, forRender, devUser, onSignedIn, onClose }: Props) {
  const titleId = useId();
  const nameId = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState(devUser || "web-dev");
  const done = useRef(onSignedIn);
  done.current = onSignedIn;
  // first focus: the name field (dev) or "Để sau" (the Google button is an iframe that arrives later), not the
  // legal links that come first in the dialog
  const nameRef = useRef<HTMLInputElement>(null);
  const laterRef = useRef<HTMLButtonElement>(null);

  const login = useCallback(async (call: () => Promise<Me>, profile: Profile | null, devName?: string) => {
    setBusy(true);
    setError(null);
    try {
      const me = await call();
      clearRef();                                     // a friend's code goes with one sign-in only (referral.ts)
      done.current(me, profile, devName);
    } catch (e) {
      setError(e instanceof ApiError ? e.code : "network");
      setBusy(false);
    }
  }, []);

  // stable: GoogleSignIn re-initialises the Google button whenever this changes
  const onGoogle = useCallback((token: string) => void login(() => api.loginGoogle(token, ...withRef()),
                                                             profileFromIdToken(token)), [api, login]);

  return (
    <Modal titleId={titleId} onClose={onClose} initialFocus={config.authMode === "dev" ? nameRef : laterRef}>
      <h2 id={titleId}>{forRender ? "Đăng nhập để xử lý ảnh" : "Đăng nhập"}</h2>
      <p>
        Mỗi ngày bạn có <strong>{FREE_DAILY} token miễn phí</strong> (nạp lại lúc 0h giờ Việt Nam), mỗi lần render
        trừ <strong>{cost} token</strong>.{adsEnabled() && " Xem quảng cáo để nhận thêm token."}
      </p>
      <p className="hint">
        Khi đăng nhập, bạn đồng ý với{" "}
        <a className="link" href={LEGAL.terms} target="_blank" rel="noopener">Điều khoản sử dụng</a> và{" "}
        <a className="link" href={LEGAL.privacy} target="_blank" rel="noopener">Chính sách quyền riêng tư</a>.
      </p>
      {forRender && <p className="hint">Ảnh và tuỳ chọn đang chọn được giữ nguyên: đăng nhập xong ảnh sẽ tự được gửi.</p>}

      {config.authMode === "dev" ? (
        <form className="dev-login" onSubmit={(e) => {
          e.preventDefault();
          const user = name.trim();
          if (!user) return;
          writeLocal("idphoto.devUser", user);
          void login(() => api.loginDev(user, ...withRef()), { name: user }, user);
        }}>
          <label htmlFor={nameId}>Tên người dùng thử (máy chủ dev)</label>
          <input ref={nameRef} id={nameId} value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
          <button type="submit" className="btn btn-primary" disabled={busy}>Đăng nhập thử (dev)</button>
        </form>
      ) : config.googleClientId ? (
        <GoogleSignIn clientId={config.googleClientId} onCredential={onGoogle} />
      ) : (
        <p className="error">Trang chưa cấu hình đăng nhập.</p>
      )}

      {busy && <p aria-live="polite">Đang đăng nhập…</p>}
      {error && <p role="alert" className="error">{errorMessage(error)}</p>}
      <div className="actions actions-end">
        <button ref={laterRef} type="button" className="btn btn-quiet" onClick={onClose}>Để sau</button>
      </div>
    </Modal>
  );
}
